import { EFFECT_TYPES, normalizeEffectType, isCausalEffect } from '../../config/effectTypes';

/**
 * Finding feedback loops the way systems modellers do it by hand.
 *
 * In a causal loop diagram every link carries a polarity — same direction (+)
 * or opposite (−) — and a closed loop is classified by one rule: count the
 * negative links. An even number (zero included) makes it REINFORCING, a
 * spiral that compounds in whichever direction it is already running. An odd
 * number makes it BALANCING, a loop that pushes back towards where it started.
 * That is the whole arithmetic; the judgement is all in the polarities, which
 * is exactly what the effect-type picker has been collecting.
 *
 * The spine of the map is a tree, so it cannot contain a loop by construction.
 * Every cycle here therefore runs through at least one overlay edge — a link
 * somebody drew back to a node that came earlier. That is the manual move a
 * modeller makes when they ask "and what does this eventually feed back into?",
 * and it stays a human act: nothing here creates edges, it only reads them.
 *
 * Delays matter to the reading and are reported per loop: a balancing loop with
 * a delay in it oscillates instead of settling, and a reinforcing one with a
 * delay looks harmless right up until it isn't.
 */

/** Loops with more nodes than this are noise rather than a story. */
const MAX_LOOP_LENGTH = 12;

const parentOf = (node) => node.parentId || node.parent_id || null;

/**
 * @returns {Array<{id, nodeIds, edges, kind: 'reinforcing'|'balancing',
 *                  label, negativeCount, hasDelay}>}
 */
export function detectFeedbackLoops(pathways = [], connections = [], blockerLinks = [], blockers = []) {
  if (!pathways.length) return [];

  // Blockers are vertices too: a ring can run through an obstacle, and that is
  // often the interesting one — the thing being blocked is what keeps the
  // blocker in place.
  const byId = new Map([
    ...pathways.map((n) => [n.id, n]),
    ...blockers.map((b) => [b.id, { id: b.id, text: b.text, isBlocker: true }]),
  ]);

  // Directed edges: the spine (parent → child, typed on the child row) plus the
  // overlay links (pathway_connections, typed on the connection itself).
  const out = new Map();
  const addEdge = (from, to, rawType) => {
    if (!from || !to || from === to) return;
    if (!byId.has(from) || !byId.has(to)) return;
    const type = normalizeEffectType(rawType);
    // A ring that needs a "liittyy" or a "sama asia" to close is not a
    // feedback loop: neither of those carries anything round. Excluding them
    // is the whole point of `causal` in the vocabulary.
    if (type && !isCausalEffect(type)) return;
    if (!out.has(from)) out.set(from, []);
    out.get(from).push({ from, to, type });
  };

  pathways.forEach((n) => addEdge(parentOf(n), n.id, n.effectType));
  // The API hands these back as { parentId, childId, type } (see
  // impactPathQueries' connections SELECT); the other spellings are for
  // rows that arrive straight off a socket event.
  connections.forEach((c) => addEdge(
    c.parentId || c.parent_node_id || c.fromNodeId || c.from,
    c.childId || c.child_node_id || c.toNodeId || c.to,
    c.type || c.connectionType || c.connection_type,
  ));

  blockerLinks.forEach((l) => addEdge(l.fromId, l.toId, l.effectType));

  // Johnson-style enumeration is overkill at workshop scale; a depth-limited
  // DFS from every node finds the same loops and stays readable.
  const seen = new Set();
  const loops = [];

  const walk = (start, current, path, edges) => {
    if (path.length > MAX_LOOP_LENGTH) return;
    for (const edge of out.get(current) || []) {
      if (edge.to === start) {
        const cycle = [...path, edge];
        // One loop, not N rotations of it: key on the sorted node set.
        const key = [...new Set(cycle.map((e) => e.from))].sort().join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        loops.push(summarize(cycle, byId));
        continue;
      }
      // Only walk forward to nodes we have not used, and only from the
      // lowest-id start, so each loop is discovered once.
      if (edge.to < start) continue;
      if (path.some((e) => e.from === edge.to)) continue;
      walk(start, edge.to, [...path, edge], edges);
    }
  };

  [...byId.keys()].forEach((id) => walk(id, id, [], []));
  return loops;
}

function summarize(cycle, byId) {
  const negativeCount = cycle.filter((e) => EFFECT_TYPES[e.type]?.polarity === '-').length;
  const hasDelay = cycle.some((e) => EFFECT_TYPES[e.type]?.delayed);
  const reinforcing = negativeCount % 2 === 0;
  return {
    id: cycle.map((e) => e.from).join('>'),
    nodeIds: cycle.map((e) => e.from),
    nodeTexts: cycle.map((e) => byId.get(e.from)?.text || byId.get(e.from)?.title || ''),
    edges: cycle,
    kind: reinforcing ? 'reinforcing' : 'balancing',
    label: reinforcing ? 'R' : 'B',
    negativeCount,
    hasDelay,
  };
}

/** Edge keys ("from>to") that take part in any loop — for highlighting. */
export function loopEdgeKeys(loops = []) {
  const keys = new Set();
  loops.forEach((l) => l.edges.forEach((e) => keys.add(`${e.from}>${e.to}`)));
  return keys;
}
